import {ClientSchemaMapperConfig} from '../../../FlowchartConfigConstants';
import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  isStaticTaskSchemaNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {
  getNodeIdPairFromTransitionSourceKey,
  getTransitionSourceByNodeIdPairKey,
} from '../../TransitionSourceUtils';
import {getFieldPairKey, getTransitionId, getPTDVPairKey} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

/**
 * Get the transition for a source/target node pair
 */
function getTransitionForNodeIdPair(
  sourceNodeId: string,
  targetNodeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): {transition: Transition; sourcePTDVId: string; targetPTDVId: string} | undefined {
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  const targetNode = nodes.find((n) => n.id === targetNodeId);

  if (!sourceNode || !targetNode) {
    return undefined;
  }
  if (!isStaticTaskSchemaNodeData(sourceNode) || !isStaticTaskSchemaNodeData(targetNode)) {
    return undefined;
  }

  const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
  const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
  const transition = Object.values(transitions).find(
    (t) =>
      t.targetPTDVId === targetPTDVId &&
      Array.isArray(t.sourcePTDVIds) &&
      t.sourcePTDVIds.includes(sourcePTDVId)
  );

  return transition != null ? {transition, sourcePTDVId, targetPTDVId} : undefined;
}

/**
 * Get custom mapping data for a data flow edge from the transitions structure.
 */
export function getCustomMappingDataForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): CustomMappingData | undefined {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return undefined;
  }

  const pair = getTransitionForNodeIdPair(edge.source, edge.target, transitions, nodes);
  if (!pair) {
    return undefined;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId: pair.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: pair.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  return pair.transition.customMappingDataByFieldPair[fieldPairKey];
}

/**
 * Get custom mapping data for a data flow edge by ID.
 */
export function getCustomMappingDataForEdge(
  edgeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): CustomMappingData | undefined {
  const edge = edges.find((e) => e.id === edgeId);
  return edge != null ? getCustomMappingDataForDataFlowEdge(edge, transitions, nodes) : undefined;
}

/**
 * Get transition source for a node pair from the transitions structure.
 */
export function getTransitionSourceForNodeIdPair(
  sourceNodeId: string,
  targetNodeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): TransitionSource | undefined {
  const pair = getTransitionForNodeIdPair(sourceNodeId, targetNodeId, transitions, nodes);
  return pair?.transition.transitionSourcesBySourcePTDVId[pair.sourcePTDVId];
}

/**
 * Build transition map from deprecated metadata fields.
 */
export function buildTransitionsFromMetadata(
  transitionSourceByNodeIdPair: Record<string, TransitionSource>,
  customMappingDataByEdgeId: Record<string, CustomMappingData>,
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): Record<string, Transition> {
  const transitions: Record<string, Transition> = {};

  // Process transition sources
  for (const [nodeIdPair, transitionSource] of Object.entries(transitionSourceByNodeIdPair)) {
    const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(nodeIdPair);
    const sourceNode = nodes.find((n) => n.id === sourceNodeId);
    const targetNode = nodes.find((n) => n.id === targetNodeId);

    if (!sourceNode || !targetNode) {
      continue;
    }
    if (!isStaticTaskSchemaNodeData(sourceNode) || !isStaticTaskSchemaNodeData(targetNode)) {
      continue;
    }

    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const transitionId = getTransitionId([sourcePTDVId], targetPTDVId);

    if (!transitions[transitionId]) {
      transitions[transitionId] = {
        id: transitionId,
        sourcePTDVIds: [sourcePTDVId],
        targetPTDVId,
        transitionSourcesBySourcePTDVId: {},
        customMappingDataByFieldPair: {},
        isDefault: false,
      };
    }

    transitions[transitionId].transitionSourcesBySourcePTDVId[sourcePTDVId] = transitionSource;
  }

  // Process custom mappings
  for (const [edgeId, customMapping] of Object.entries(customMappingDataByEdgeId)) {
    const edge = edges.find((e) => e.id === edgeId);
    if (!edge) {
      continue;
    }
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      continue;
    }

    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      continue;
    }
    if (!isStaticTaskSchemaNodeData(sourceNode) || !isStaticTaskSchemaNodeData(targetNode)) {
      continue;
    }

    const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const transitionId = getTransitionId([sourcePTDVId], targetPTDVId);

    if (!transitions[transitionId]) {
      transitions[transitionId] = {
        id: transitionId,
        sourcePTDVIds: [sourcePTDVId],
        targetPTDVId,
        transitionSourcesBySourcePTDVId: {},
        customMappingDataByFieldPair: {},
        isDefault: false,
      };
    }

    const fieldPairKey = getFieldPairKey({
      sourcePTDVId,
      sourceSectionType: sourceHandle.sectionType,
      sourceFieldClientId: sourceHandle.fieldClientId,
      targetPTDVId,
      targetSectionType: targetHandle.sectionType,
      targetFieldClientId: targetHandle.fieldClientId,
    });

    transitions[transitionId].customMappingDataByFieldPair[fieldPairKey] = customMapping;
  }

  return transitions;
}

/**
 * Build old metadata format from new transitions structure.
 */
export function buildMetadataFromTransitions(
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): {
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
} {
  const transitionSourceByNodeIdPair: Record<string, TransitionSource> = {};
  const customMappingDataByEdgeId: Record<string, CustomMappingData> = {};

  // Build PTDV ID to node ID mapping
  const ptdvIdToNodeIds: Record<string, Array<string>> = {};
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      const ptdvId = node.taskDefinitionVersionSpec.clientId;
      if (!ptdvIdToNodeIds[ptdvId]) {
        ptdvIdToNodeIds[ptdvId] = [];
      }
      ptdvIdToNodeIds[ptdvId].push(node.id);
    }
  }

  // Process transition sources back to node-pair format
  for (const transition of Object.values(transitions)) {
    for (const [sourcePTDVId, transitionSource] of Object.entries(
      transition.transitionSourcesBySourcePTDVId
    )) {
      const sourceNodeIds = ptdvIdToNodeIds[sourcePTDVId] ?? [];
      const targetNodeIds = ptdvIdToNodeIds[transition.targetPTDVId] ?? [];

      for (const sourceNodeId of sourceNodeIds) {
        for (const targetNodeId of targetNodeIds) {
          const key = getTransitionSourceByNodeIdPairKey(sourceNodeId, targetNodeId);
          transitionSourceByNodeIdPair[key] = transitionSource;
        }
      }
    }
  }

  // Process custom mappings back to edge-based format
  for (const edge of edges) {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      continue;
    }

    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      continue;
    }
    if (!isStaticTaskSchemaNodeData(sourceNode) || !isStaticTaskSchemaNodeData(targetNode)) {
      continue;
    }

    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
    const transition = Object.values(transitions).find(
      (t) =>
        t.sourcePTDVIds.includes(sourcePTDVId) &&
        t.targetPTDVId === targetPTDVId &&
        getPTDVPairKey(sourcePTDVId, t.targetPTDVId) === pairKey
    );

    if (!transition) {
      continue;
    }

    const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    const fieldPairKey = getFieldPairKey({
      sourcePTDVId,
      sourceSectionType: sourceHandle.sectionType,
      sourceFieldClientId: sourceHandle.fieldClientId,
      targetPTDVId,
      targetSectionType: targetHandle.sectionType,
      targetFieldClientId: targetHandle.fieldClientId,
    });

    const customMapping = transition.customMappingDataByFieldPair[fieldPairKey];
    if (customMapping) {
      customMappingDataByEdgeId[edge.id] = customMapping;
    }
  }

  return {
    transitionSourceByNodeIdPair,
    customMappingDataByEdgeId,
  };
}

/**
 * Apply double-write to sync transitions to deprecated fields in a partial metadata update.
 * This should be called before dispatching the update to ensure both local ref and Redux
 * state receive consistent data.
 *
 * TODO(BNCH-141054): Remove this once migration to PTDV-based transitions is complete.
 *
 * @param partialMetadata - The partial metadata being updated
 * @param nodes - The flowchart nodes (from updated staticFlowchart or current state)
 * @param edges - The flowchart edges (from updated staticFlowchart or current state)
 * @returns The partial metadata with deprecated fields synced from transitions
 */
export function applyTransitionDoubleWriteToPartialMetadata<
  T extends {transitions?: Record<string, Transition>},
>(partialMetadata: T, nodes: Array<StaticFlowchartNodeData>, edges: Array<FlowchartStaticEdgeData>): T {
  if (partialMetadata.transitions == null) {
    return partialMetadata;
  }

  const {transitionSourceByNodeIdPair, customMappingDataByEdgeId} = buildMetadataFromTransitions(
    partialMetadata.transitions,
    nodes,
    edges
  );

  return {
    ...partialMetadata,
    transitionSourceByNodeIdPair,
    customMappingDataByEdgeId,
  };
}
